import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import { ActiveQuestionnairesByElementDocument } from "@/graphql/generated/operations";
import { BackButton } from "@/components/common/BackButton";
import { QuestionnaireRunner } from "@/components/grow/QuestionnaireRunner";
import { ELEMENT_CODES, elementImageSrc } from "@/lib/elements";

// Element-bound questionnaire page: resolves the power from the route, pulls
// the questionnaire meta from the element's active list and hands the actual
// answering UI to QuestionnaireRunner. Reached from a power's "Fragebögen"
// list at /grow/power/:abbr/questionnaire/:questionnaireId.

// ELEMENT_CODES maps element → code; the route param is the code.
const ELEMENT_BY_CODE = new Map(Object.entries(ELEMENT_CODES).map(([el, code]) => [code, el]));

export function QuestionnaireAssessmentPage() {
  const navigate = useNavigate();
  const { abbr = "", questionnaireId = "" } = useParams();
  const element = ELEMENT_BY_CODE.get(abbr) ?? null;

  // Questionnaire meta (name / instructions) from the element's active list.
  const { data: metaData } = useQuery(ActiveQuestionnairesByElementDocument, {
    variables: { element: element as never },
    skip: !element,
  });
  const meta = (metaData?.activeQuestionnairesByElement ?? []).find(
    (q) => q.questionnaire.questionnaireId === questionnaireId,
  )?.questionnaire;

  if (!element) {
    return (
      <section className="mx-auto max-w-screen-md px-4 py-12">
        <BackButton />
        <h1 id="grow-unknown-element" className="smycles-page-title mt-4">
          <FormattedMessage id="grow.questionnaire.unknownElement" defaultMessage="Unknown element" />
        </h1>
        <p className="mt-2 text-smycles-body text-ink-muted">{abbr}</p>
      </section>
    );
  }

  return (
    <section
      className="mx-auto max-w-screen-md px-4 py-12"
      data-graphql="QuestionnaireQuestions, ActiveQuestionnairesByElement, SaveQuestionnaireResults"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      <QuestionnaireRunner
        questionnaireId={questionnaireId}
        name={meta?.name}
        instructions={meta?.instructions}
        eyebrow={<FormattedMessage id="grow.questionnaire.eyebrow" defaultMessage="Grow · Questionnaire" />}
        revealSrc={elementImageSrc(element)}
        onSubmitted={() => navigate(`/grow/power/${abbr}`)}
      />
    </section>
  );
}
